"use client"

import styled from "@emotion/styled"
import { CarType, useComparison } from "@eurodrive/ui"
import { EngineGroup, FinishLevel, Link, PackageOption } from "@eurodrive/utils"
import { Button, Typography } from "@mui/material"
import { LeasingCarDto, SaleCarDto } from "api"
import { Config, EngineOption, formatPrice } from "./CarConfiguration"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const InfoLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const TopBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding-top: 18px;
  border-top: 1px solid #ece9e9;
`

const BottomBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 24px;
  border-top: 1px solid #ece9e9;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PricesFlex = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export default function ThirdStage({
  colors,
  config,
  data,
  options,
  engineGroups = [],
  hasColors = true,
  finishLevels = [],
  additionalPackages = [],
  isLeasing = false,
}: {
  colors: {
    color: string
    label: string
    price: number
  }[]
  options: EngineOption[]
  engineGroups?: EngineGroup[]
  config: Config
  data: SaleCarDto | LeasingCarDto
  hasColors?: boolean
  finishLevels?: FinishLevel[]
  additionalPackages?: PackageOption[]
  isLeasing?: boolean
}) {
  const comparison = useComparison()

  // For sales with engine groups, get data from selected trim
  const selectedEngineGroup = !isLeasing && engineGroups.length > 0 && config.engine !== null
    ? engineGroups[config.engine]
    : null
  const selectedTrim = selectedEngineGroup && config.trim !== null
    ? selectedEngineGroup.trims[config.trim]
    : null

  // Build currentOption from trim data or fall back to engineOptions
  const currentOption = selectedTrim
    ? {
        price: selectedTrim.price,
        oldPrice: selectedTrim.oldPrice,
        engine: selectedEngineGroup!.engineLabel,
        vehicleNumber: selectedTrim.vehicleNumber,
        listPrice: selectedTrim.listPrice,
      }
    : options[config.engine!]

  // For sales with engine groups, calculate trim delta from the base (cheapest) trim
  const baseTrimPrice = selectedEngineGroup && selectedEngineGroup.trims.length > 0
    ? selectedEngineGroup.trims[0].price
    : 0
  const trimDelta = selectedTrim && selectedEngineGroup
    ? selectedTrim.price - baseTrimPrice
    : 0
  // The engine base price is the cheapest trim's advertisedPrice for that engine
  const engineBasePrice = selectedEngineGroup
    ? baseTrimPrice
    : currentOption.price

  const currentColor =
    hasColors && config.color !== null ? colors[config.color] : null
  const colorPrice = currentColor ? currentColor.price : 0

  // Leasing-specific price calculations
  const currentFinishLevel =
    isLeasing &&
    config.finishLevel !== null &&
    config.finishLevel !== undefined &&
    finishLevels.length > 0
      ? finishLevels[config.finishLevel]
      : null
  const finishLevelPrice = currentFinishLevel
    ? currentFinishLevel.monthlyPrice
    : 0

  const selectedPackage =
    isLeasing &&
    config.packages !== null &&
    config.packages !== undefined &&
    additionalPackages.length > 0
      ? additionalPackages[config.packages]
      : null
  const packagesPrice = selectedPackage?.monthlyPrice || 0

  const registrationFee = 2000
  const basePrice =
    registrationFee + colorPrice + currentOption.price + finishLevelPrice + packagesPrice

  const oldPrice = currentOption.oldPrice
    ? registrationFee +
      colorPrice +
      currentOption.oldPrice +
      finishLevelPrice +
      packagesPrice
    : null
  const newPrice = basePrice

  // Determine car type for comparison
  const carType: CarType = isLeasing ? "leasing" : "sale"

  // Generate car ID for checking if already in comparison
  const carId = isLeasing
    ? `leasing-${(data as LeasingCarDto).manufacturerCode}-${(data as LeasingCarDto).modelCode}`
    : `sale-${(data as SaleCarDto).vehicleNumber}`

  const isInComparison = comparison.isCarSelected(carId)

  // Hand the configured vehicle to the contact form the same way the vehicle
  // page does: the lead type drives the subject options and the ESSENSE code,
  // and the model name travels in the remarks, never as the subject itself.
  // The two lead types read the name from different fields, so this has to
  // match its own vehicle page or sales sees two spellings for one car:
  // LeasingPage sends carZone.name.modelName, CarPage sends modelName.
  const leadModelName =
    (isLeasing
      ? (data as LeasingCarDto).carZone?.name?.modelName
      : (data as SaleCarDto).modelName) || ""
  const contactUsHref = `/contact-us?carLeadType=${
    isLeasing ? "leasing" : "sale"
  }&modelName=${encodeURIComponent(leadModelName)}`

  const handleComparisonClick = () => {
    if (isInComparison) {
      // If already in comparison, navigate to comparison page
      const comparisonUrl = comparison.getComparisonUrl()
      if (comparisonUrl) {
        window.location.href = comparisonUrl
      }
    } else {
      // Add to comparison
      const added = comparison.addCar(data, carType)
      if (added) {
        // Optionally, navigate to comparison page immediately
        // or show a success message
        const comparisonUrl = comparison.getComparisonUrl()
        if (comparisonUrl) {
          window.location.href = comparisonUrl
        }
      }
    }
  }

  return (
    <StyledLayout>
      <Typography variant="body1">סיכום ההזמנה</Typography>
      <InfoLayout>
        <TopBox>
          <Row>
            <Typography variant="body2">
              מחיר {data.carZone?.name.makeNameEn}{" "}
              {data.carZone?.name?.modelNameEn}{" "}
              {currentOption.engine}
            </Typography>
            <Typography variant="body2">
              {formatPrice(engineBasePrice)}
            </Typography>
          </Row>
          {/* Show trim level for sales cars with multiple trims */}
          {!isLeasing && selectedTrim && selectedEngineGroup && selectedEngineGroup.trims.length > 1 && (
            <Row>
              <Typography variant="body2">
                רמת גימור {selectedTrim.trimLevelDisplay}
              </Typography>
              <Typography variant="body2">
                {trimDelta > 0
                  ? `+${formatPrice(trimDelta)}`
                  : "כלול במחיר"}
              </Typography>
            </Row>
          )}
          {hasColors && currentColor && (
            <Row>
              <Typography variant="body2">צבע {currentColor.label}</Typography>
              <Typography variant="body2">
                {currentColor.price > 0
                  ? formatPrice(currentColor.price)
                  : "ללא עלות"}
              </Typography>
            </Row>
          )}
          {isLeasing && currentFinishLevel && (
            <Row>
              <Typography variant="body2">
                רמת גימור {currentFinishLevel.name}
              </Typography>
              <Typography variant="body2">
                {currentFinishLevel.monthlyPrice > 0
                  ? formatPrice(currentFinishLevel.monthlyPrice)
                  : "ללא עלות"}
              </Typography>
            </Row>
          )}
          {isLeasing && selectedPackage && (
            <Row>
              <Typography variant="body2">{selectedPackage.name}</Typography>
              <Typography variant="body2">
                {selectedPackage.monthlyPrice > 0
                  ? formatPrice(selectedPackage.monthlyPrice)
                  : "ללא עלות"}
              </Typography>
            </Row>
          )}
          <Row>
            <Typography variant="body2">אגרת רישוי</Typography>
            <Typography variant="body2">₪{formatPrice(registrationFee)}</Typography>
          </Row>
        </TopBox>
        <BottomBox>
          <Row>
            <Typography variant="body2" fontWeight={600}>
              {isLeasing ? "מחיר חודשי" : "סה״כ לתשלום"}
            </Typography>
            <PricesFlex>
              {oldPrice && (
                <Typography
                  variant="body3"
                  style={{ textDecoration: "line-through" }}
                >
                  {formatPrice(oldPrice)}
                </Typography>
              )}
              <Typography variant="h3">{formatPrice(newPrice)}</Typography>
            </PricesFlex>
          </Row>
        </BottomBox>
        {currentOption.listPrice ? (
          <Row>
            <Typography variant="body2" color={"#6F6F6F"}>
              מחיר מחירון
            </Typography>
            <Typography variant="body2" color={"#6F6F6F"}>
              {formatPrice(currentOption.listPrice)}
            </Typography>
          </Row>
        ) : null}
      </InfoLayout>
      <ButtonsContainer>
        <Button
          variant="primary"
          size="large"
          fullWidth
          LinkComponent={Link}
          href={contactUsHref}
        >
          השארת פרטים
        </Button>
        <Button
          variant="secondary"
          size="large"
          fullWidth
          onClick={handleComparisonClick}
        >
          {isInComparison ? "מעבר להשוואה" : "להשוואת רכבים"}
        </Button>
      </ButtonsContainer>
    </StyledLayout>
  )
}
